import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import FormWithProvider from 'components/forms/form/Form';
import useAppUser from 'hooks/useAppUser';
import { Alert } from 'react-bootstrap';
import useFormatCurrency from 'hooks/useFormatCurrency';
import { useScreenAndProtectContext } from '../ScreenAndProtect';
import DisplayValue from '../common/displayValue/DisplayValue';
import {
  CancelProtectionButtonStyled,
  ScreenAndProtectContainer,
  ScreenAndProtectHero,
  ScreenAndProtectSection,
  ScreenAndProtectSectionsContainer,
} from '../common/ScreenAndProtectCommon.styles';
import useCancelProtectionModal from './cancel/useCancelProtectionModal';
import ViewProtectedProperties from './viewProtectedProperties/ViewProtectedProperties';

const ScreenAndProtectCurrent = () => {
  const { isEmployeeManager } = useAppUser();
  const { isEnrolled, activeSince } = useScreenAndProtectContext();
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();
  const { openCancelProtectionModal } = useCancelProtectionModal();

  if (!isEmployeeManager) {
    return (
      <Alert bsStyle="warning">
        {t('pageScreenAndProtect.currentPage.noPermission')}
      </Alert>
    );
  }

  if (!isEnrolled) {
    return <Navigate to="/screen-and-protect" replace />;
  }

  const handleCancelProtection = () => {
    openCancelProtectionModal();
  };

  return (
    <ScreenAndProtectContainer
      className="container"
      data-testid="screen-and-protect-current"
    >
      <ScreenAndProtectHero>
        <h1>{t('pageScreenAndProtect.currentPage.hero.title')}</h1>
        <p>{t('pageScreenAndProtect.currentPage.hero.subtitle')}</p>
      </ScreenAndProtectHero>

      <ScreenAndProtectSectionsContainer>
        <FormWithProvider onSubmit={handleCancelProtection}>
          <ScreenAndProtectSection data-testid="screen-and-protect-cost">
            <h3>
              {t(
                'pageScreenAndProtect.currentPage.sections.screenAndProtectCost.title',
              )}
            </h3>

            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.screenAndProtectCost.approvedOrFlaggedBookings',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.screenAndProtectCost.costApprovedOrFlagged',
                    {
                      cost: formatCurrency({
                        value: 8.5,
                        currency: 'USD',
                        options: { maximumFractionDigits: 1 },
                      }),
                    },
                  )}
                </span>
              }
              valueSize="large"
              valueDataIntercomTarget="current-screen-protect-approved-booking-cost"
            />

            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.screenAndProtectCost.approvedOrFlaggedBookingsPlusNights',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.screenAndProtectCost.costApprovedOrFlagged',
                    { cost: formatCurrency({ value: 5, currency: 'USD' }) },
                  )}
                </span>
              }
              valueSize="large"
              valueDataIntercomTarget="current-screen-protect-approved-booking-31-plus-cost"
            />

            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.screenAndProtectCost.rejectedOrCancelledBookings',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.screenAndProtectCost.costRejectedOrCancelled',
                    { cost: formatCurrency({ value: 1, currency: 'USD' }) },
                  )}
                </span>
              }
              valueSize="large"
              valueDataIntercomTarget="current-screen-protect-rejected-booking-cost"
            />
          </ScreenAndProtectSection>

          <ScreenAndProtectSection data-testid="maximum-protection-payment">
            <h3>
              {t(
                'pageScreenAndProtect.currentPage.sections.maximumProtectionPayment.title',
              )}
            </h3>
            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.maximumProtectionPayment.approvedBooking',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.maximumProtectionPayment.maxApprovedPayment',
                    {
                      maxPayment: formatCurrency({
                        value: 50000,
                        currency: 'USD',
                      }),
                    },
                  )}
                </span>
              }
              valueSize="large"
              valueDataIntercomTarget="current-screen-protect-approved-coverage"
            />
            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.maximumProtectionPayment.flaggedBooking',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.maximumProtectionPayment.maxFlaggedPayment',
                    {
                      maxPayment: formatCurrency({
                        value: 1000,
                        currency: 'USD',
                      }),
                    },
                  )}
                </span>
              }
              valueSize="large"
              valueDataIntercomTarget="current-screen-protect-flagged-coverage"
            />
          </ScreenAndProtectSection>

          <ScreenAndProtectSection data-testid="protection-details">
            <h3>
              {t(
                'pageScreenAndProtect.currentPage.sections.protectionDetails.title',
              )}
            </h3>

            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.protectionDetails.bookingChannels',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.protectionDetails.bookingChannelsValue',
                  )}
                </span>
              }
              valueDataIntercomTarget="current-screen-protect-booking-channels"
            />

            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.protectionDetails.properties',
              )}
              value={<ViewProtectedProperties />}
            />

            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.protectionDetails.reservations',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.protectionDetails.reservationsValue',
                  )}
                </span>
              }
              helper={t(
                'pageScreenAndProtect.currentPage.sections.protectionDetails.reservationsValueHelper',
              )}
              valueDataIntercomTarget="current-screen-protect-reservations"
            />

            <DisplayValue
              label={t(
                'pageScreenAndProtect.currentPage.sections.protectionDetails.activeSince',
              )}
              value={
                <span>
                  {t(
                    'pageScreenAndProtect.currentPage.sections.protectionDetails.activeSinceValue',
                    {
                      date: activeSince ? parseISO(activeSince) : '-',
                    },
                  )}
                </span>
              }
              valueDataIntercomTarget="current-screen-protect-start-date"
            />
          </ScreenAndProtectSection>

          <CancelProtectionButtonStyled
            type="submit"
            bsStyle="danger"
            data-testid="screen-and-protect-current-cancel-protection-button"
          >
            {t('pageScreenAndProtect.currentPage.actions.cancelProtection')}
          </CancelProtectionButtonStyled>
        </FormWithProvider>
      </ScreenAndProtectSectionsContainer>
    </ScreenAndProtectContainer>
  );
};

export default ScreenAndProtectCurrent;
